import {
  DEBT_SORT_LABELS,
  agingLevel,
  fmt,
  matchesCustomer,
  sortCustomers,
  type AgingLevel,
  type Customer,
  type DebtSort,
} from '@k2pos/core';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppBar } from '@/components/ui/AppBar';
import { Card } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { Mono, Text } from '@/components/ui/Text';
import { cx } from '@/lib/cx';
import { totalDebt, usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Debt.module.css';

const SORTS: DebtSort[] = ['oldest', 'largest', 'all'];

/** ໃຜຕິດແດ່ — everyone who owes, sorted the way the owner thinks about it. */
export function Debt() {
  const customers = usePos((s) => s.customers);
  const [sort, setSort] = useState<DebtSort>('oldest');
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    // One shared predicate with the cart's picker: a shopkeeper who finds
    // someone on one screen and not the other concludes the app lost them.
    const matching = customers.filter((c) => c.balance > 0 && matchesCustomer(c, query));
    return sortCustomers(matching, sort);
  }, [customers, sort, query]);

  return (
    <>
      <AppBar
        title="ໃຜຕິດແດ່"
        subtitle={
          <Text size={11} color={colors.onDarkSoft}>
            ໜີ້ຄ້າງ{' '}
            <Mono weight={700} size={13} color={colors.sky}>
              {fmt(totalDebt(customers))}
            </Mono>{' '}
            ₭
          </Text>
        }
      />

      <div className={styles.controls}>
        <Field
          value={query}
          onChange={setQuery}
          placeholder="ຄົ້ນຫາຊື່ ຫຼື ເບີໂທ..."
          label="ຄົ້ນຫາລູກຄ້າ"
        />
        <div className={styles.sorts} role="tablist">
          {SORTS.map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={option === sort}
              onClick={() => setSort(option)}
              className={cx(styles.sortChip, option === sort && styles.sortChipOn)}
            >
              <Text
                weight={option === sort ? 800 : 500}
                size={11.5}
                color={option === sort ? colors.surfaceAlt : colors.textMuted}
              >
                {DEBT_SORT_LABELS[option]}
              </Text>
            </button>
          ))}
        </div>
      </div>

      <div className={`${styles.list} vscroll`}>
        {rows.map((customer) => (
          <DebtorRow key={customer.id} customer={customer} />
        ))}

        {rows.length === 0 ? (
          <Text size={12} color={colors.textMuted} align="center" className={styles.empty}>
            ບໍ່ມີໜີ້ຄ້າງ · ດີຫຼາຍ
          </Text>
        ) : null}
      </div>
    </>
  );
}

function DebtorRow({ customer }: { customer: Customer }) {
  const level = agingLevel(customer.days);

  return (
    <Link to={`/customer/${customer.id}`} className={styles.rowLink}>
      <Card accent={ACCENT[level]} className={styles.row}>
        <div className="grow col">
          <Text weight={700} size={14} lines={1}>
            {customer.name}
          </Text>
          <Mono size={11} color={colors.textMuted} lines={1}>
            {customer.phone}
          </Mono>
        </div>

        <div className={styles.rowRight}>
          <Mono weight={700} size={14} className="selectable">
            {fmt(customer.balance)}
          </Mono>
          <span className={cx(styles.pill, PILL[level])}>
            <Mono weight={700} size={10} color={PILL_TEXT[level]}>
              {customer.days} ມື້
            </Mono>
          </span>
        </div>
      </Card>
    </Link>
  );
}

/** Aging thresholds carry the urgency: >30 ມື້ reads darkest, <15 ມື້ stays quiet. */
const ACCENT: Record<AgingLevel, string> = {
  old: colors.navy,
  mid: colors.skyDeep,
  fresh: colors.borderMid,
};

const PILL: Record<AgingLevel, string | undefined> = {
  old: styles.pillOld,
  mid: styles.pillMid,
  fresh: styles.pillFresh,
};
const PILL_TEXT: Record<AgingLevel, string> = {
  old: colors.sky,
  mid: colors.skyDeep,
  fresh: colors.textMuted,
};
